import { useState } from 'react'
import { Fuel, BookOpen, Package, DollarSign, Building, User } from 'lucide-react'
import CreditSaleForm from '../components/credit/CreditSaleForm'
import CreditLedger from '../components/credit/CreditLedger'
import WeeklyBatchPanel from '../components/credit/WeeklyBatchPanel'
import PriceManagementPanel from '../components/credit/PriceManagementPanel'
import TransportCompaniesPanel from '../components/credit/TransportCompaniesPanel'
import DriversPanel from '../components/credit/DriversPanel'

// Prices leads: the day's price is what every credit sale is written against,
// so admins confirm it before looking at anything else.
const TABS = [
  { id: 'prices', label: 'Prices', icon: DollarSign },
  { id: 'sale', label: 'New Sale', icon: Fuel },
  { id: 'ledger', label: 'Ledger', icon: BookOpen },
  { id: 'batches', label: 'Batches', icon: Package },
  { id: 'companies', label: 'Companies', icon: Building },
  { id: 'drivers', label: 'Drivers', icon: User },
]

export default function CreditPage({ currentUser, creditModule }) {
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'superadmin'
  const [activeTab, setActiveTab] = useState(isAdmin ? 'prices' : 'sale')

  const {
    companies, drivers, prices, priceHistory, sales, batches,
    recordSale, editSale, updatePrice, bulkImportSales,
    addCompany, updateCompany, toggleCompany,
    addDriver, updateDriver, toggleDriver,
    exportBatch, reconcileBatch,
    getPastDestinations,
    branches,
  } = creditModule

  // Tab visibility by role:
  //   admin/superadmin — oversight: prices, ledger, batches, companies, drivers
  //   manager          — sale, ledger, batches, companies, drivers
  //   staff            — sale, ledger, companies, drivers
  const tabIds =
    currentUser.role === 'manager' ? ['sale', 'ledger', 'batches', 'companies', 'drivers'] :
    currentUser.role === 'staff'   ? ['sale', 'ledger', 'companies', 'drivers'] :
                                     ['prices', 'ledger', 'batches', 'companies', 'drivers']
  const visibleTabs = TABS.filter((t) => tabIds.includes(t.id))

  return (
    <div className="-mx-4 sm:-mx-6 -mt-4 sm:-mt-6">
      {/* Sticky sub-nav */}
      <div className="sticky top-14 sm:top-16 z-10 flex overflow-x-auto border-b border-gray-200 bg-white px-2 pt-2 scrollbar-hide">
        {visibleTabs.map((tab) => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center px-3 pb-2 pt-1 mr-1 border-b-2 flex-shrink-0 transition ${
                isActive
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4 mb-0.5" />
              <span className="text-xs font-medium whitespace-nowrap">{tab.label}</span>
            </button>
          )
        })}
      </div>

      {/* Content — natural page scroll, pb-28 from AppShell covers bottom nav */}
      <div className="px-4 sm:px-6 pt-4">
        {activeTab === 'sale' && (
          <CreditSaleForm
            currentUser={currentUser}
            branches={branches}
            companies={companies}
            drivers={drivers}
            prices={prices}
            onRecord={recordSale}
            getPastDestinations={getPastDestinations}
            onAddCompany={addCompany}
            onAddDriver={addDriver}
          />
        )}
        {activeTab === 'ledger' && (
          <CreditLedger
            currentUser={currentUser}
            branches={branches}
            companies={companies}
            drivers={drivers}
            prices={prices}
            sales={sales}
            onBulkImport={bulkImportSales}
            onEdit={editSale}
          />
        )}
        {activeTab === 'batches' && (
          <WeeklyBatchPanel
            currentUser={currentUser}
            branches={branches}
            batches={batches}
            sales={sales}
            onExport={exportBatch}
            onReconcile={reconcileBatch}
          />
        )}
        {activeTab === 'prices' && isAdmin && (
          <PriceManagementPanel
            currentUser={currentUser}
            branches={branches}
            prices={prices}
            priceHistory={priceHistory}
            onUpdatePrice={updatePrice}
          />
        )}
        {activeTab === 'companies' && (
          <TransportCompaniesPanel
            currentUser={currentUser}
            branches={branches}
            companies={companies}
            onAdd={addCompany}
            onUpdate={updateCompany}
            onToggle={toggleCompany}
          />
        )}
        {activeTab === 'drivers' && (
          <DriversPanel
            currentUser={currentUser}
            branches={branches}
            drivers={drivers}
            sales={sales}
            onAdd={addDriver}
            onUpdate={updateDriver}
            onToggle={toggleDriver}
          />
        )}
      </div>
    </div>
  )
}
